package ui.campaign.detailv2.editor.page

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.Crossfade
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.*
import io.github.alexzhirkevich.qrose.QrCodePainter
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.type.ScreenRatio
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tools.EFieldColorAsLong
import tools.EFieldDualIntAsFloat
import tools.rememberLazyListState
import tools.rememberString
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.NextButton
import ui.campaign.detailv2.editor.widget.AddActionButton
import ui.campaign.detailv2.editor.widget.CampaignConditionHeader
import ui.campaign.detailv2.editor.widget.ExplainUi
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperEApplication
import ui.widget.*


@Composable
fun CampaignEditorScope.CampaignPage06Application(nnvl: CampaignWrapperE) {

    val scrollState = nav.rememberLazyListState("campaign-editor-apps")
    val scope = rememberCoroutineScope()
    val appPickerState = DialogState.remember()

    DialogAppPicker(nnvl, appPickerState) {
        nnvl.apps.addApp(it)
        scope.launch {
            scrollState.animateScrollToItem(0)
        }
    }

    Box(Modifier.fillMaxSize()) {
        Crossfade(
            targetState = nnvl.apps.adapterApps.state.isEmpty(),
            modifier = Modifier.fillMaxSize()
        ) { noConditions ->
            if (noConditions) ExplainUi(Res.string.layer_top_detail_editor__application_info)
            else ContentUi(nnvl, scrollState)
        }

        AddActionButton {
            appPickerState.show()
        }
    }
}


@Composable
private fun CampaignEditorScope.ContentUi(nnvl: CampaignWrapperE, scrollState: LazyListState) {

    var showOverlayLoader by remember {
        mutableStateOf(false)
    }

    LazyColumn(
        state = scrollState,
        contentPadding =
            if (display.isMobileOrSmall) PaddingValues(vertical = 16.dp)
            else PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.fillMaxSize()
    ) {
        items(nnvl.apps.adapterApps.state) { item ->
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainer
                )
            ) {
                with(item.conditions) {
                    CampaignConditionHeader(item.key)
                    ItemContent(nnvl, item)
                }
            }
        }
        item {
            CampaignBehavior(nnvl)
        }
        item {
            NextButton()
        }
    }

    AnimatedVisibility(
        visible = showOverlayLoader,
        modifier = Modifier.fillMaxSize(),
        enter = fadeIn(),
        exit = fadeOut()
    ) {
        Box(
            Modifier.fillMaxSize()
                .background(Color.Black.copy(alpha = 0.7F))
                .blockTouch()
        ) {
            CircularProgressIndicator(Modifier.align(Alignment.Center))
        }
    }
}

@Composable
private fun ColumnScope.ItemContent(
    nnvl: CampaignWrapperE,
    item: CampaignWrapperEApplication.AdapterAddedApps.Item
) {
    val display = LocalDisplayProvider.current

    Row(
        modifier = Modifier.fillMaxWidth()
            .background(MaterialTheme.colorScheme.surfaceContainerHigh)
            .padding(8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        LazyImage(
            id = item.data.logo,
            modifier = Modifier.size(64.dp),
            alphaBackground = false
        )
        Text(
            text = item.data.name,
            modifier = Modifier.align(Alignment.CenterVertically)
                .weight(1F),
            style = MaterialTheme.typography.titleMedium,
            maxLines = 2
        )
        TextButton(
            onClick = { nnvl.apps.adapterApps.removeAll { it.key == item.key } },
            modifier = Modifier.align(Alignment.CenterVertically)
        ) {
            Icon(Svg.Delete_outline, null)
        }
    }
    Space(16.dp)
    if (display.isMobileOrSmall) {
        ColorField(
            field = item.colorBG,
            label = Res.string.layer_top_detail_editor__apps_item_color_bg,
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
        )
        Space(8.dp)
        ColorField(
            field = item.colorFG,
            label = Res.string.layer_top_detail_editor__apps_item_color_fg,
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
        )
    } else {
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
        ) {
            ColorField(
                field = item.colorBG,
                label = Res.string.layer_top_detail_editor__apps_item_color_bg,
                modifier = Modifier.width(256.dp)
            )
            ColorField(
                field = item.colorFG,
                label = Res.string.layer_top_detail_editor__apps_item_color_fg,
                modifier = Modifier.width(256.dp)
            )
        }
    }
    val horizontal = remember {
        nnvl.visual.extract()
            .firstOrNull { it.isPrimary }
            ?.res
            ?.firstOrNull { it.isHorizontal }
            ?.let { ScreenRatio[it.screenRatio] to it.resourceId }
    }
    val vertical = remember {
        nnvl.visual.extract()
            .firstOrNull { it.isPrimary }
            ?.res
            ?.firstOrNull { !it.isHorizontal }
            ?.let { ScreenRatio[it.screenRatio] to it.resourceId }
    }
    val code = rememberQrCodePainter("https://game.screensoft.org")
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.align(Alignment.CenterHorizontally)
            .padding(16.dp)
    ) {
        horizontal?.also {
            CardItemSetup(
                config = item.codeLand,
                fg = item.colorFG,
                bg = item.colorBG,
                entry = it,
                code = code
            )
        }
        vertical?.also {
            if (display.isMobileOrSmall && horizontal != null) {
                HorizontalDivider(Modifier.padding(horizontal = 8.dp))
                Space(8.dp)
            }
            CardItemSetup(
                config = item.codePort,
                fg = item.colorFG,
                bg = item.colorBG,
                entry = it,
                code = code
            )
        }
    }
}

@Composable
private fun CampaignEditorScope.CampaignBehavior(nnvl: CampaignWrapperE) {
    val tooltipState = DialogState.remember(key = "CampaignBehavior-tooltip")
    TooltipDialog(
        state = tooltipState,
        title = Res.string.layer_top_detail_editor__apps_playback_setup_title,
        message = Res.string.layer_top_detail_editor__apps_playback_setup_tooltip
    )
    val modifier =
        if (display.isMobileOrSmall) Modifier.fillMaxWidth()
        else Modifier.width(384.dp)

    Box(Modifier.fillMaxWidth()) {
        Card(modifier.align(Alignment.Center)) {
            Column(
                modifier = Modifier.padding(8.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Row {
                    Text(
                        text = Res.string.layer_top_detail_editor__apps_playback_setup_title.rememberString(),
                        style = MaterialTheme.typography.titleLarge,
                        modifier = Modifier.weight(1F)
                            .align(Alignment.CenterVertically)
                            .padding(start = 8.dp)
                    )
                    IconButton({ tooltipState.show() }) {
                        Icon(Svg.Chat_question, null)
                    }
                }
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth().clip(CardDefaults.shape)
                        .clickable { nnvl.apps.appBehaviorCampaignOverlap.set(true) }
                        .padding(4.dp)) {
                    RadioButton(
                        nnvl.apps.appBehaviorCampaignOverlap.state,
                        { nnvl.apps.appBehaviorCampaignOverlap.set(true) }
                    )
                    Text(
                        text = Res.string.layer_top_detail_editor__apps_playback_setup_overlap.rememberString(),
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                }
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth().clip(CardDefaults.shape)
                        .clickable { nnvl.apps.appBehaviorReduceImpressions.set(true) }
                        .padding(4.dp)) {
                    RadioButton(
                        nnvl.apps.appBehaviorReduceImpressions.state,
                        { nnvl.apps.appBehaviorReduceImpressions.set(true) }
                    )
                    Text(
                        text = Res.string.layer_top_detail_editor__apps_playback_setup_reduction.rememberString(),
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                }
            }
        }
    }
}

@Composable
private fun CardItemSetup(
    config: CampaignWrapperEApplication.AdapterAddedApps.Item.QrConfig,
    entry: Pair<ScreenRatio, Long>,
    code: QrCodePainter,
    fg: EFieldColorAsLong,
    bg: EFieldColorAsLong
) {
    val display = LocalDisplayProvider.current
    val sliders: @Composable ColumnScope.() -> Unit = {
        FieldSlider(
            icon = Svg.Aspect_ratio,
            label = Res.string.layer_top_detail_editor__apps_item_size,
            field = config.size,
            valueRange = 0.1f..1f
        )
        FieldSlider(
            icon = Svg.Relative_scale,
            label = Res.string.layer_top_detail_editor__apps_item_padding,
            field = config.offset,
            valueRange = 0.01f..0.5f
        )
        FieldSlider(
            icon = Svg.Pan_horizontal,
            label = Res.string.layer_top_detail_editor__apps_item_x,
            field = config.x
        )
        FieldSlider(
            icon = Svg.Pan_vertical,
            label = Res.string.layer_top_detail_editor__apps_item_y,
            field = config.y
        )
    }

    if (display.isMobileOrSmall) {
        CodeRenderer(
            modifier = Modifier.fillMaxWidth(),
            entry = entry,
            config = config,
            bg = bg,
            code = code,
            fg = fg
        )
        Column(
            modifier = Modifier
                .padding(horizontal = 8.dp)
                .fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            sliders()
        }
    } else {
        Card(Modifier.padding(8.dp)) {
            Row(modifier = Modifier) {
                CodeRenderer(
                    modifier = Modifier.height(256.dp).align(Alignment.CenterVertically),
                    entry = entry,
                    config = config,
                    bg = bg,
                    code = code,
                    fg = fg
                )
                Column(
                    modifier = Modifier
                        .padding(horizontal = 8.dp)
                        .width(384.dp)
                        .align(Alignment.CenterVertically),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    sliders()
                }
            }
        }
    }
}

@Composable
private fun CodeRenderer(
    entry: Pair<ScreenRatio, Long>,
    config: CampaignWrapperEApplication.AdapterAddedApps.Item.QrConfig,
    bg: EFieldColorAsLong,
    code: QrCodePainter,
    fg: EFieldColorAsLong,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .aspectRatio(
                ratio = entry.first.pair.first.toFloat() / entry.first.pair.second.toFloat(),
                matchHeightConstraintsFirst = true
            )
    ) {
        LazyImage(
            id = entry.second,
            modifier = Modifier.fillMaxSize(),
            alphaBackground = false,
            contentScale = ContentScale.FillBounds
        )

        Canvas(Modifier.fillMaxSize()) {
            val qrSize = ((config.size.state) * minOf(
                size.width,
                size.height
            ))
            val padding = qrSize * (config.offset.state / 2F)

            clipRect {
                translate(
                    ((size.width * (config.x.state)) - (qrSize / 2F)),
                    ((size.height * (config.y.state)) - (qrSize / 2F))
                ) {
                    drawRoundRect(
                        color = Color(bg.state.toArgb()),
                        size = Size(qrSize, qrSize),
                        cornerRadius = CornerRadius(padding)
                    )
                    translate(padding, padding) {
                        with(code) {
                            val ratio = (qrSize - (padding * 2F)) / intrinsicSize.width
                            scale(ratio, Offset.Zero) {
                                draw(
                                    size = intrinsicSize,
                                    colorFilter = ColorFilter.tint(Color(fg.state.toArgb()))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CampaignEditorScope.DialogAppPicker(
    nnvl: CampaignWrapperE,
    state: DialogState,
    onSelected: (Io.ListingApp.Item) -> Unit
) {
    Dialog(state) {
        val scrollState = rememberScrollState()
        val apps = nnvl.apps.actualApps.items
            .filter { it.id !in nnvl.apps.adapterApps.state.map { it.data.appId } }
            .sortedByDescending { it.id }
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { state.hide() },
            title = { Text(Res.string.layer_top_detail_editor__apps_pick_app_title.rememberString()) },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_close.rememberString())
                }
            }
        ) {
            if (apps.isEmpty()) {
                Text(Res.string.layer_top_detail_editor__apps_pick_app_msg.rememberString())
            } else {
                FlowRow(
                    modifier = Modifier.fillMaxWidth()
                        .wrapContentHeight(Alignment.Top)
                        .verticalScroll(scrollState),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    maxItemsInEachRow = if (display.isMobileOrSmall) 1 else 2
                ) {
                    apps.forEach { item ->
                        Card(
                            modifier = Modifier.weight(1F),
                            onClick = {
                                onSelected(item)
                                state.hide()
                            }) {
                            Row(
                                modifier = Modifier.fillMaxWidth()
                                    .padding(4.dp),
                                horizontalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                LazyImage(
                                    id = item.logo,
                                    modifier = Modifier.size(48.dp),
                                    alphaBackground = false
                                )
                                Text(
                                    text = item.name,
                                    maxLines = 2,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier.align(Alignment.CenterVertically)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun FieldSlider(
    field: EFieldDualIntAsFloat,
    icon: ImageVector,
    label: StringResource,
    valueRange: ClosedFloatingPointRange<Float> = 0.01f..1f
) {
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Box(Modifier.align(Alignment.CenterVertically)) {
            TooltipBox(
                positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
                tooltip = {
                    ElevatedCard {
                        Text(label.rememberString(), modifier = Modifier.padding(8.dp))
                    }
                },
                state = CustomTooltipState.rememberState(),
                focusable = false,
                enableUserInput = true,
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null
                )
            }
        }

        Slider(
            value = field.state,
            valueRange = valueRange,
            modifier = Modifier.fillMaxWidth(),
            onValueChange = {
                field.set(it)
            })
    }
}